/**
 * Camera QR scanner (qr-scanner, loaded on demand; rear camera). Explains camera problems in
 * plain words; the caller always offers "Type code instead".
 */
import { useEffect, useRef, useState } from "react";

type ScannerState = "starting" | "running" | "error";

function cameraError(e: unknown): string {
  const name = (e as { name?: string } | null)?.name ?? "";
  const text = String((e as { message?: string } | null)?.message ?? e);
  if (name === "NotAllowedError" || /denied|permission/i.test(text)) {
    return "Camera access was blocked. Allow the camera for this site in your browser settings, or type the code instead.";
  }
  if (name === "NotFoundError" || /not found|no camera/i.test(text)) {
    return "No camera was found. Type the code instead.";
  }
  if (name === "NotReadableError") {
    return "The camera is busy in another app. Close it, or type the code instead.";
  }
  return "The camera could not start. Type the code instead.";
}

export function Scanner({ onResult }: { onResult: (text: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const handler = useRef(onResult);
  const [state, setState] = useState<ScannerState>("starting");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handler.current = onResult;
  }, [onResult]);

  useEffect(() => {
    let cancelled = false;
    let scanner: { destroy(): void } | null = null;
    const fail = (message: string) => {
      if (cancelled) return;
      setState("error");
      setError(message);
    };
    void (async () => {
      if (!window.isSecureContext) {
        fail("The camera needs a secure (https) connection. Type the code instead.");
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        fail("This browser cannot use the camera. Type the code instead.");
        return;
      }
      try {
        const { default: QrScanner } = await import("qr-scanner");
        if (cancelled || !video.current) return;
        const s = new QrScanner(video.current, (r) => handler.current(r.data), {
          preferredCamera: "environment",
          maxScansPerSecond: 8,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          returnDetailedScanResult: true,
        });
        scanner = s;
        await s.start();
        if (cancelled) {
          s.destroy();
          return;
        }
        setState("running");
      } catch (e) {
        fail(cameraError(e));
      }
    })();
    return () => {
      cancelled = true;
      scanner?.destroy();
    };
  }, []);

  return (
    <div className="stack">
      <div className="scanner" aria-label="Camera view">
        <video ref={video} muted playsInline />
      </div>
      {state === "starting" ? (
        <p className="muted center">Starting the camera… allow access when your phone asks.</p>
      ) : null}
      {state === "running" ? (
        <p className="muted center">Point the camera at your partner's code.</p>
      ) : null}
      {error ? (
        <p className="error-text center" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
